import { Layout } from "@/components/layout/Layout";
import { getStrapiCollection, getStrapiEntity } from "@/utils/strapi";
import { sendFetchRequest } from "@/utils/sendFetchRequest";
import {
  LoadI18nMessagesProps,
  loadIntlMessages,
} from "@/utils/loadIntlMessages";
import { createQueryAgencyPage } from "@/strapiQuery/agency-page";
import {
  formatFirstAgencyPage,
  StrapiAgencyPage,
} from "@/utils/formatAgencyPage";
import { NetworkError } from "@/interfaces/errors/networkError";
import { page404 } from "@/const/page404";
import { AgencyDetails } from "@/interfaces/agencyDetails";
import { DefaultPresentationProps } from "@/components/common/defaultPresentation/DefaultPresentation";
import { queryDefaultPresentation } from "@/strapiQuery/default-presentation";
import { routes } from "@/routes/routes";
import { ReviewsConst } from "@/components/common/reviewsBlock/ReviewsBlock";
import { queryReviewsConst } from "@/strapiQuery/queryReviewsConst";

type GetStaticPropsParams = LoadI18nMessagesProps & {
  params: { slug: string };
};

const proximityAgenciesRadius =
  process.env.NEXT_PUBLIC_PROXIMITY_AGENCIES_RADIUS ?? "";
const maxProximityAgencies =
  process.env.NEXT_PUBLIC_MAX_PROXIMITY_AGENCIES ?? "";

const SLUG_NOT_FOUND_ERROR_CODE = 422;

export const getStaticProps = async (ctx: GetStaticPropsParams) => {
  const layout = await getStrapiEntity<Layout>("layout", "");
  const reviewsConst = await getStrapiEntity<ReviewsConst>(
    "reviews-const",
    queryReviewsConst
  );
  const intlMessages = loadIntlMessages(ctx);

  let agency: AgencyDetails;
  try {
    agency = await sendFetchRequest(
      `/agencies/v1?slug=${ctx.params.slug}&radius=${proximityAgenciesRadius}&maxProximityAgency=${maxProximityAgencies}`
    ).then((json: AgencyDetails) => json);
  } catch (error) {
    if (
      error instanceof NetworkError &&
      error.statusCode === SLUG_NOT_FOUND_ERROR_CODE
    ) {
      return page404;
    }
    throw error;
  }

  const strapiDefaultPresentation =
    await getStrapiEntity<DefaultPresentationProps>(
      "default-agency-presentation",
      queryDefaultPresentation
    );

  const strapiAgencies = await getStrapiCollection<StrapiAgencyPage>(
    "agency-pages",
    createQueryAgencyPage(ctx.params.slug)
  );
  if (strapiAgencies.length === 0) {
    return page404;
  }
  const agencyPageContent = formatFirstAgencyPage(strapiAgencies);

  return {
    props: {
      layout,
      reviewsConst,
      intlMessages,
      slug: ctx.params.slug,
      agency,
      heroBlock: agencyPageContent.heroBlock,
      defaultPresentation: strapiDefaultPresentation,
      seo: agencyPageContent.seo,
      seoWithTemplate: agencyPageContent.seoWithTemplate,
      pointOfInterestList: agencyPageContent.pointOfInterestList,
      agencyDescription: agencyPageContent.agencyDescription,
      faq: agencyPageContent.faq,
      defaultCanonicalRoute: routes.agencyPage(ctx.params.slug),
    },
    revalidate: 10,
  };
};

export const getStaticPaths = () => {
  return {
    paths: [],
    fallback: "blocking",
  };
};

export { AgencyPage as default } from "@/components/pages/agencies/agencyPage/AgencyPage";
